/**
 * What the login screen says when a login fails, and why it says it.
 *
 * Split out of Login.tsx so the branches can be exercised without a browser. The bug that prompted
 * them was a 403 reported as a network failure — the kind of thing a unit test catches in a second
 * and a visual test only catches if somebody thought to stub that particular status.
 */

/** The one case where being explicit beats being vague: locking yourself out deserves a reason. */
export const RATE_LIMITED = "TOO MANY ATTEMPTS. TRY AGAIN IN 15 MINUTES.";
/** One message for both fields: naming the wrong one tells the caller the other was right. */
export const WRONG = "THAT USERNAME AND PASSWORD DO NOT MATCH.";
/** Only for a request that got no answer at all — `api.ts` throws status 0 for exactly that. */
export const OFFLINE = "THE SERVER DIDN'T RESPOND. CHECK THE CONNECTION AND TRY AGAIN.";

/**
 * The message for a failed login.
 *
 * Every branch that is not 401 or 429 used to fall to OFFLINE, which made "the server didn't
 * respond" the app's answer to a 403 from the origin check and to a 500 from a database that went
 * away. Neither is a server that did not respond, and reporting them that way sends the one person
 * who can fix it to look at his network. So: a request that got no answer says so, and everything
 * the server actually answered says what it answered.
 */
export function loginError(status: number, code?: string, origin?: string): string {
  if (status === 0) return OFFLINE;
  if (status === 401) return WRONG;
  if (status === 429) return RATE_LIMITED;

  // The CSRF origin check (apps/api/src/guard.ts). The person reading this owns the server, so the
  // message is the fix rather than a description of it — and the value to paste is the address he
  // is already looking at.
  if (status === 403 && code === "bad_origin") {
    return `THE SERVER REFUSED THIS ADDRESS. SET PUBLIC_ORIGIN TO ${origin ?? "THIS ORIGIN"} AND REDEPLOY.`;
  }

  return `THE SERVER ANSWERED ${status}. THAT IS NOT A WRONG PASSWORD — CHECK THE SERVER LOG.`;
}
